import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, DollarSign, TrendingDown, Edit, Trash2, Sparkles } from "lucide-react";

const Finance = () => {
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]); // Add classes state
  const [loading, setLoading] = useState(true);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditExpenseDialogOpen, setIsEditExpenseDialogOpen] = useState(false);
  const [isDeleteExpenseDialogOpen, setIsDeleteExpenseDialogOpen] = useState(false);
  const [isGenerateInvoicesOpen, setIsGenerateInvoicesOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateType, setGenerateType] = useState("tuition");
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [expenseFilter, setExpenseFilter] = useState("all");
  const [studentFilter, setStudentFilter] = useState("all"); // Add student filter state
  const [sessionFilter, setSessionFilter] = useState("all"); // Add session filter state
  const [invoiceDateFilter, setInvoiceDateFilter] = useState(new Date().toISOString().slice(0, 7)); // Current month (YYYY-MM)
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all"); // All statuses
  const [expenseDateFilter, setExpenseDateFilter] = useState(new Date().toISOString().slice(0, 7)); // Current month (YYYY-MM)
  
  const [invoiceForm, setInvoiceForm] = useState({
    student_id: "",
    amount: "",
    type: "tuition",
    description: "",
    due_date: new Date().toISOString().split("T")[0],
    status: "pending",
    payment_method: null,
  });
  
  const [editForm, setEditForm] = useState({
    amount: "",
    type: "",
    description: "",
    due_date: "",
    status: "",
    payment_method: "",
  });
  
  const [expenseForm, setExpenseForm] = useState({
    category: "supplies",
    amount: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [editExpenseForm, setEditExpenseForm] = useState({
    category: "supplies",
    amount: "",
    description: "",
    date: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, expensesRes, studentsRes, classesRes] = await Promise.all([
        axios.get(`${API}/finance/invoices`),
        axios.get(`${API}/finance/expenses`),
        axios.get(`${API}/students`),
        axios.get(`${API}/classes`),
      ]);
      setInvoices(invoicesRes.data);
      setExpenses(expensesRes.data);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error("Failed to load finance data");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get class name from class_id
  const getClassName = (classId) => {
    const classObj = classes.find(cls => cls.id === classId);
    return classObj ? classObj.name : 'Unknown Class';
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount),
        payment_method: invoiceForm.payment_method === "" ? null : invoiceForm.payment_method
      };
      await axios.post(`${API}/finance/invoices`, submitData);
      toast.success("Invoice created successfully");
      fetchData();
      setIsInvoiceDialogOpen(false);
      resetInvoiceForm();
    } catch (error) {
      console.error("Invoice creation error:", error.response?.data || error);
      toast.error("Failed to create invoice");
    }
  };

  const handleGenerateMonthlyInvoices = async () => {
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/finance/invoices/generate-monthly`, {
        month: invoiceDateFilter,
        type: generateType,
      });
      const { created, skipped_existing, total_active_students } = res.data;
      if (created === 0) {
        toast.info(
          `No invoices generated. ${skipped_existing} student(s) already have a ${generateType} invoice for this month.`
        );
      } else {
        toast.success(
          `✅ Generated ${created} invoice(s) — skipped ${skipped_existing} already existing (${total_active_students} active students total).`
        );
      }
      fetchData();
      setIsGenerateInvoicesOpen(false);
    } catch (error) {
      console.error("Generate monthly invoices error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to generate monthly invoices");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        amount: parseFloat(editForm.amount),
        type: editForm.type,
        description: editForm.description,
        due_date: editForm.due_date,
        status: editForm.status,
        payment_method: editForm.status === "paid" ? editForm.payment_method : null,
      };
      await axios.put(`${API}/finance/invoices/${editingInvoice.id}`, submitData);
      toast.success("Invoice updated successfully");
      fetchData();
      setIsEditDialogOpen(false);
      setEditingInvoice(null);
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const handleStatusToggle = async (invoice) => {
    if (invoice.status === "endorsed") {
      toast.info("Endorsed invoices cannot be modified");
      return;
    }

    const newStatus = invoice.status === "paid" ? "pending" : "paid";
    
    try {
      await axios.put(`${API}/finance/invoices/${invoice.id}`, {
        status: newStatus,
        payment_method: newStatus === "paid" ? "cash" : null,
      });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/finance/invoices/${invoiceId}`);
      toast.success("Invoice deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const openEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      amount: invoice.amount.toString(),
      type: invoice.type,
      description: invoice.description || "",
      due_date: invoice.due_date,
      status: invoice.status,
      payment_method: invoice.payment_method || "",
    });
    setIsEditDialogOpen(true);
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/finance/expenses`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
      });
      toast.success("Expense added successfully");
      fetchData();
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const handleEditExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/finance/expenses/${editingExpense.id}`, {
        ...editExpenseForm,
        amount: parseFloat(editExpenseForm.amount),
      });
      toast.success("✅ Expense updated successfully!");
      fetchData();
      setIsEditExpenseDialogOpen(false);
      resetEditExpenseForm();
    } catch (error) {
      toast.error("Failed to update expense");
    }
  };

  const handleDeleteExpense = async () => {
    try {
      await axios.delete(`${API}/finance/expenses/${deletingExpense.id}`);
      toast.success("🗑️ Expense deleted successfully");
      fetchData();
      setIsDeleteExpenseDialogOpen(false);
      setDeletingExpense(null);
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditExpenseForm({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description,
      date: expense.date,
    });
    setIsEditExpenseDialogOpen(true);
  };

  const openDeleteExpense = (expense) => {
    setDeletingExpense(expense);
    setIsDeleteExpenseDialogOpen(true);
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      student_id: "",
      amount: "",
      type: "tuition",
      description: "",
      due_date: new Date().toISOString().split("T")[0],
      status: "pending",
      payment_method: null,
    });
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      category: "supplies",
      amount: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
    });
  };

  const resetEditExpenseForm = () => {
    setEditExpenseForm({
      category: "supplies",
      amount: "",
      description: "",
      date: "",
    });
    setEditingExpense(null);
  };

  const getFeeTypeBadge = (feeType, discountPercentage) => {
    const baseClass = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium";
    
    if (feeType === "full") {
      return (
        <span className={`${baseClass} bg-green-100 text-green-700`}>
          <span className="mr-1">🟢</span> Full
        </span>
      );
    } else if (feeType === "discounted") {
      return (
        <span className={`${baseClass} bg-amber-100 text-amber-700`}>
          <span className="mr-1">🟡</span> {discountPercentage}% Off
        </span>
      );
    } else if (feeType === "endorsed") {
      return (
        <span className={`${baseClass} bg-blue-100 text-blue-700`}>
          <span className="mr-1">🔵</span> Sponsored
        </span>
      );
    }
  };

  const getStatusBadge = (status, onClick) => {
    const isClickable = status !== "endorsed";
    const baseClass = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium";
    const clickableClass = isClickable ? "cursor-pointer hover:opacity-80 transition-opacity" : "cursor-not-allowed";
    
    if (status === "paid") {
      return (
        <span 
          className={`${baseClass} ${clickableClass} bg-green-100 text-green-700`}
          onClick={onClick}
          data-testid="status-badge-paid"
        >
          <span className="mr-1">🟢</span> Paid
        </span>
      );
    } else if (status === "pending") {
      return (
        <span 
          className={`${baseClass} ${clickableClass} bg-yellow-100 text-yellow-700`}
          onClick={onClick}
          data-testid="status-badge-pending"
        >
          <span className="mr-1">🟡</span> Pending
        </span>
      );
    } else if (status === "endorsed") {
      return (
        <span 
          className={`${baseClass} ${clickableClass} bg-gray-100 text-gray-600`}
          data-testid="status-badge-endorsed"
        >
          <span className="mr-1">⚪</span> Endorsed
        </span>
      );
    }
  };

  const getPaymentMethodBadge = (method) => {
    if (!method) return <span className="text-xs text-gray-400">-</span>;
    
    const baseClass = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium";
    
    const badges = {
      cash: <span className={`${baseClass} bg-[#E4C987] text-[#1E624A]`}><span className="mr-1">💵</span> Cash</span>,
      bank: <span className={`${baseClass} bg-blue-100 text-blue-700`}><span className="mr-1">🏦</span> Bank</span>,
      zaad: <span className={`${baseClass} bg-green-100 text-green-700`}><span className="mr-1">💳</span> Zaad</span>,
      edahab: <span className={`${baseClass} bg-orange-100 text-orange-700`}><span className="mr-1">📱</span> E-Dahab</span>,
      other: <span className={`${baseClass} bg-gray-100 text-gray-700`}><span className="mr-1">🧾</span> Other</span>,
    };
    
    return badges[method] || badges.other;
  };

  const calculateFinancials = () => {
    let totalBilled = 0;
    let totalCollected = 0;
    let expectedRevenue = 0;

    // Filter invoices by selected month
    const filteredInvoices = invoices.filter((invoice) => {
      if (!invoice.due_date) return true;
      const invoiceMonth = invoice.due_date.slice(0, 7); // Extract YYYY-MM
      return invoiceMonth === invoiceDateFilter;
    });

    filteredInvoices.forEach((invoice) => {
      const student = students.find((s) => s.id === invoice.student_id);
      if (!student) return;

      if (student.fee_type !== "endorsed") {
        let invoiceAmount = invoice.amount;
        
        if (student.fee_type === "discounted" && student.discount_percentage) {
          invoiceAmount = invoiceAmount * (1 - student.discount_percentage / 100);
        }

        totalBilled += invoiceAmount;
        expectedRevenue += invoiceAmount;

        if (invoice.status === "paid") {
          totalCollected += invoiceAmount;
        }
      }
    });

    // Filter expenses by selected month
    const filteredExpenses = expenses.filter(expense => {
      if (!expense.date) return true;
      const expenseMonth = expense.date.slice(0, 7); // Extract YYYY-MM
      return expenseMonth === expenseDateFilter;
    });

    const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const netIncome = totalCollected - totalExpenses;

    // Student counts should reflect students with invoices in the selected month
    const studentsWithFilteredInvoices = [...new Set(filteredInvoices.map(inv => inv.student_id))];
    const fullPayingCount = studentsWithFilteredInvoices.filter(studentId => {
      const student = students.find(s => s.id === studentId);
      return student && student.fee_type === "full";
    }).length;
    const discountedCount = studentsWithFilteredInvoices.filter(studentId => {
      const student = students.find(s => s.id === studentId);
      return student && student.fee_type === "discounted";
    }).length;
    const endorsedCount = studentsWithFilteredInvoices.filter(studentId => {
      const student = students.find(s => s.id === studentId);
      return student && student.fee_type === "endorsed";
    }).length;

    return {
      totalBilled: totalBilled.toFixed(2),
      totalCollected: totalCollected.toFixed(2),
      expectedRevenue: expectedRevenue.toFixed(2),
      totalExpenses: totalExpenses.toFixed(2),
      netIncome: netIncome.toFixed(2),
      fullPayingCount,
      discountedCount,
      endorsedCount,
      filteredInvoicesCount: filteredInvoices.length,
      filteredExpensesCount: filteredExpenses.length,
    };
  };

  const financials = calculateFinancials();

  // Trigger recalculation when filters change
  useEffect(() => {
    // This effect ensures financials are recalculated when filters change
  }, [invoiceDateFilter, expenseDateFilter, invoices, expenses, students]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1E624A]">Finance Management</h2>
        <p className="text-sm text-gray-600">Maamulka Maaliyadda</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md" data-testid="total-billed-card">
          <div className="flex items-center gap-3">
            <DollarSign className="text-blue-500" size={32} />
            <div>
              <p className="text-sm text-gray-600">Expected Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${financials.expectedRevenue}</p>
              <p className="text-xs text-gray-500 mt-1">
                {invoiceDateFilter ? `For ${new Date(invoiceDateFilter + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'All months'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md" data-testid="total-collected-card">
          <div className="flex items-center gap-3">
            <DollarSign className="text-green-500" size={32} />
            <div>
              <p className="text-sm text-gray-600">Total Collected</p>
              <p className="text-2xl font-bold text-gray-900">${financials.totalCollected}</p>
              <p className="text-xs text-gray-500 mt-1">
                {invoiceDateFilter ? `${new Date(invoiceDateFilter + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'With discounts applied'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md" data-testid="total-expenses-card">
          <div className="flex items-center gap-3">
            <TrendingDown className="text-red-500" size={32} />
            <div>
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">${financials.totalExpenses}</p>
              <p className="text-xs text-gray-500 mt-1">
                {expenseDateFilter ? `${new Date(expenseDateFilter + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'All expenses'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md" data-testid="net-income-card">
          <div className="flex items-center gap-3">
            <DollarSign className="text-[#1E624A]" size={32} />
            <div>
              <p className="text-sm text-gray-600">Net Income</p>
              <p className="text-2xl font-bold text-gray-900">${financials.netIncome}</p>
              <p className="text-xs text-gray-500 mt-1">
                Revenue - Expenses
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Student Fee Breakdown
          <span className="text-sm text-gray-500 font-normal ml-2">
            ({invoiceDateFilter ? `${new Date(invoiceDateFilter + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'All months'})
          </span>
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{financials.fullPayingCount}</p>
            <p className="text-sm text-gray-600 mt-1">💰 Full Paying</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{financials.discountedCount}</p>
            <p className="text-sm text-gray-600 mt-1">💸 Discounted</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{financials.endorsedCount}</p>
            <p className="text-sm text-gray-600 mt-1">🕌 Endorsed</p>
          </div>
        </div>
        {financials.filteredInvoicesCount !== undefined && (
          <p className="text-xs text-gray-500 mt-3 text-center">
            Based on {financials.filteredInvoicesCount} invoices in selected period
          </p>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="invoices-tab">
            Invoices / Xisaabaha
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">
            Expenses / Kharashaadka
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <Button
              data-testid="generate-monthly-invoices-btn"
              onClick={() => {
                setGenerateType("tuition");
                setIsGenerateInvoicesOpen(true);
              }}
              variant="outline"
              className="border-[#1E624A] text-[#1E624A] hover:bg-[#1E624A]/5"
            >
              <Sparkles size={18} className="mr-2" />
              Generate Monthly Invoices
            </Button>
            <Dialog
              open={isInvoiceDialogOpen}
              onOpenChange={setIsInvoiceDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="add-invoice-btn"
                  onClick={resetInvoiceForm}
                  className="bg-[#1E624A] hover:bg-[#164935]"
                >
                  <Plus size={20} className="mr-2" />
                  Add Invoice
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Invoice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvoiceSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="student">Student *</Label>
                    <Select
                      value={invoiceForm.student_id}
                      onValueChange={(value) =>
                        setInvoiceForm({ ...invoiceForm, student_id: value })
                      }
                    >
                      <SelectTrigger data-testid="invoice-student-select">
                        <SelectValue placeholder="Select student" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map((student) => (
                          <SelectItem key={student.id} value={student.id}>
                            {student.name} {getFeeTypeBadge(student.fee_type, student.discount_percentage)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={invoiceForm.type}
                      onValueChange={(value) =>
                        setInvoiceForm({ ...invoiceForm, type: value })
                      }
                    >
                      <SelectTrigger data-testid="invoice-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tuition">Tuition</SelectItem>
                        <SelectItem value="books">Books</SelectItem>
                        <SelectItem value="uniform">Uniform</SelectItem>
                        <SelectItem value="transport">Transport</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount ($) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      data-testid="invoice-amount-input"
                      value={invoiceForm.amount}
                      onChange={(e) =>
                        setInvoiceForm({ ...invoiceForm, amount: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      data-testid="invoice-description-input"
                      value={invoiceForm.description}
                      onChange={(e) =>
                        setInvoiceForm({ ...invoiceForm, description: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      type="date"
                      data-testid="invoice-due-date-input"
                      value={invoiceForm.due_date}
                      onChange={(e) =>
                        setInvoiceForm({ ...invoiceForm, due_date: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="cancel-invoice-btn"
                      onClick={() => setIsInvoiceDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-invoice-btn"
                      className="bg-[#1E624A] hover:bg-[#164935]"
                    >
                      Create Invoice
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Generate Monthly Invoices Confirmation Dialog */}
          <Dialog open={isGenerateInvoicesOpen} onOpenChange={setIsGenerateInvoicesOpen}>
            <DialogContent data-testid="generate-monthly-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles size={20} className="text-[#1E624A]" />
                  Generate Monthly Invoices
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-[#E4C987]/10 border border-[#E4C987]/40 rounded-lg p-4 text-sm text-gray-700">
                  This will create one invoice per <strong>active student</strong> for{" "}
                  <strong>
                    {invoiceDateFilter
                      ? new Date(invoiceDateFilter + "-01").toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })
                      : "the selected month"}
                  </strong>
                  , using each student&apos;s own <em>fee amount</em> from their profile.
                  <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600">
                    <li>Endorsed students → invoice created with amount $0 and status <em>Endorsed</em></li>
                    <li>Students who already have an invoice of this type for this month → skipped</li>
                    <li>Due date will be set to the 1st of the selected month</li>
                  </ul>
                </div>

                <div>
                  <Label htmlFor="generate-type">Invoice Type</Label>
                  <Select value={generateType} onValueChange={setGenerateType}>
                    <SelectTrigger id="generate-type" data-testid="generate-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tuition">Tuition</SelectItem>
                      <SelectItem value="books">Books</SelectItem>
                      <SelectItem value="uniform">Uniform</SelectItem>
                      <SelectItem value="transport">Transport</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="cancel-generate-monthly-btn"
                    onClick={() => setIsGenerateInvoicesOpen(false)}
                    disabled={isGenerating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    data-testid="confirm-generate-monthly-btn"
                    onClick={handleGenerateMonthlyInvoices}
                    disabled={isGenerating}
                    className="bg-[#1E624A] hover:bg-[#164935]"
                  >
                    {isGenerating ? "Generating..." : "Generate Invoices"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="w-full lg:w-60">
              <Label htmlFor="student-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                Filter by Student
              </Label>
              <Select value={studentFilter} onValueChange={setStudentFilter}>
                <SelectTrigger data-testid="student-filter-select" className="bg-white">
                  <SelectValue placeholder="All Students" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Students</SelectItem>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.name} - {getClassName(student.class_id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-full lg:w-60">
              <Label htmlFor="session-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                Filter by Session
              </Label>
              <Select value={sessionFilter} onValueChange={setSessionFilter}>
                <SelectTrigger data-testid="session-filter-select" className="bg-white">
                  <SelectValue placeholder="All Sessions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sessions</SelectItem>
                  <SelectItem value="morning">☀️ Morning</SelectItem>
                  <SelectItem value="afternoon">🌇 Afternoon</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-full lg:w-60">
              <Label htmlFor="invoice-date-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                Filter by Month
              </Label>
              <input
                type="month"
                id="invoice-date-filter"
                value={invoiceDateFilter}
                onChange={(e) => setInvoiceDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E624A] focus:border-transparent"
              />
            </div>

            <div className="w-full lg:w-60">
              <Label htmlFor="invoice-status-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                Filter by Status
              </Label>
              <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                <SelectTrigger data-testid="invoice-status-filter-select" className="bg-white">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">🟡 Pending</SelectItem>
                  <SelectItem value="paid">🟢 Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Student</th>
                    <th className="hidden sm:table-cell px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Fee Type</th>
                    <th className="hidden md:table-cell px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                    <th className="px-2 sm:px-4 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                    <th className="hidden lg:table-cell px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Due Date</th>
                    <th className="px-2 sm:px-4 py-3.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="hidden md:table-cell px-2 sm:px-4 py-3.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Payment Method</th>
                    <th className="px-2 sm:px-4 py-3.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoices
                    .filter((invoice) => studentFilter === "all" || invoice.student_id === studentFilter)
                    .filter((invoice) => {
                      if (sessionFilter === "all") return true;
                      const student = students.find((s) => s.id === invoice.student_id);
                      return student && student.session === sessionFilter;
                    })
                    .filter((invoice) => {
                      if (!invoice.due_date) return true;
                      const invoiceMonth = invoice.due_date.slice(0, 7); // Extract YYYY-MM
                      return invoiceMonth === invoiceDateFilter;
                    })
                    .filter((invoice) => invoiceStatusFilter === "all" || invoice.status === invoiceStatusFilter)
                    .map((invoice) => {
                    const student = students.find((s) => s.id === invoice.student_id);
                    let displayAmount = invoice.amount;
                    let originalAmount = invoice.amount;
                    
                    if (student && student.fee_type === "discounted" && student.discount_percentage) {
                      displayAmount = invoice.amount * (1 - student.discount_percentage / 100);
                    }
                    
                    if (student && student.fee_type === "endorsed") {
                      displayAmount = 0;
                    }
                    
                    return (
                      <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 sm:px-4 py-4 align-middle">
                          <span className="font-medium text-gray-900">{student?.name || "Unknown"}</span>
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-4 align-middle">
                          {student && getFeeTypeBadge(student.fee_type, student.discount_percentage)}
                        </td>
                        <td className="hidden md:table-cell px-2 sm:px-4 py-4 align-middle">
                          <span className="capitalize text-gray-700">{invoice.type}</span>
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-right align-middle">
                          <div className="flex flex-col items-end">
                            <span className="font-semibold text-gray-900">
                              ${displayAmount.toFixed(2)}
                            </span>
                            {student && student.fee_type === "discounted" && student.discount_percentage && (
                              <span className="text-xs text-gray-500 line-through">
                                was ${originalAmount.toFixed(2)}
                              </span>
                            )}
                            {student && student.fee_type === "endorsed" && (
                              <span className="text-xs text-blue-600 font-medium">
                                Sponsored
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="hidden lg:table-cell px-2 sm:px-4 py-4 align-middle">
                          <span className="text-sm text-gray-700">{invoice.due_date}</span>
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-center align-middle">
                          {getStatusBadge(invoice.status, () => handleStatusToggle(invoice))}
                        </td>
                        <td className="hidden md:table-cell px-2 sm:px-4 py-4 text-center align-middle">
                          {getPaymentMethodBadge(invoice.payment_method)}
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-center align-middle">
                          <div className="flex gap-2 justify-center">
                            <Button
                              data-testid={`edit-invoice-${invoice.id}-btn`}
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(invoice)}
                              className="h-8 w-8 p-0 bg-white shadow-sm hover:bg-gray-50"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              data-testid={`delete-invoice-${invoice.id}-btn`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="h-8 w-8 p-0 bg-white shadow-sm hover:bg-red-50 hover:border-red-200"
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invoices.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No invoices found
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-end mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-64">
                <Label htmlFor="expense-category-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                  Filter by Category
                </Label>
                <Select value={expenseFilter} onValueChange={setExpenseFilter}>
                  <SelectTrigger data-testid="expense-category-filter-select" className="bg-white">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🗂️ All Categories</SelectItem>
                    <SelectItem value="supplies">📚 Supplies</SelectItem>
                    <SelectItem value="utilities">⚡ Utilities</SelectItem>
                    <SelectItem value="salaries">👥 Salaries</SelectItem>
                    <SelectItem value="maintenance">🔧 Maintenance</SelectItem>
                    <SelectItem value="bus_maintenance">🚌 Bus Maintenance</SelectItem>
                    <SelectItem value="fuel">⛽ Fuel</SelectItem>
                    <SelectItem value="other">📋 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-64">
                <Label htmlFor="expense-date-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                  Filter by Month
                </Label>
                <input
                  type="month"
                  id="expense-date-filter"
                  value={expenseDateFilter}
                  onChange={(e) => setExpenseDateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E624A] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-end">
              <h3 className="text-lg font-semibold text-[#1E624A]">
                Expenses / <span className="text-sm text-gray-600">Kharashaadka</span>
              </h3>
            </div>
            <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#1E624A] hover:bg-[#164935]"
                  data-testid="add-expense-btn"
                  onClick={resetExpenseForm}
                >
                  <Plus size={20} className="mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleExpenseSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={expenseForm.category}
                      onValueChange={(value) =>
                        setExpenseForm({ ...expenseForm, category: value })
                      }
                    >
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supplies">📚 Supplies</SelectItem>
                        <SelectItem value="utilities">⚡ Utilities</SelectItem>
                        <SelectItem value="salaries">👥 Salaries</SelectItem>
                        <SelectItem value="maintenance">🔧 Maintenance</SelectItem>
                        <SelectItem value="bus_maintenance">🚌 Bus Maintenance</SelectItem>
                        <SelectItem value="fuel">⛽ Fuel</SelectItem>
                        <SelectItem value="other">📋 Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="exp_amount">Amount ($) *</Label>
                    <Input
                      id="exp_amount"
                      type="number"
                      step="0.01"
                      data-testid="expense-amount-input"
                      value={expenseForm.amount}
                      onChange={(e) =>
                        setExpenseForm({ ...expenseForm, amount: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="exp_description">Description *</Label>
                    <Input
                      id="exp_description"
                      data-testid="expense-description-input"
                      value={expenseForm.description}
                      onChange={(e) =>
                        setExpenseForm({ ...expenseForm, description: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="exp_date">Date *</Label>
                    <Input
                      id="exp_date"
                      type="date"
                      data-testid="expense-date-input"
                      value={expenseForm.date}
                      onChange={(e) =>
                        setExpenseForm({ ...expenseForm, date: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="cancel-expense-btn"
                      onClick={() => setIsExpenseDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-expense-btn"
                      className="bg-[#1E624A] hover:bg-[#164935]"
                    >
                      Add Expense
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                    <th className="hidden md:table-cell px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                    <th className="px-2 sm:px-4 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                    <th className="hidden sm:table-cell px-2 sm:px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-2 sm:px-4 py-3.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {expenses
                    .filter(expense => expenseFilter === "all" || expense.category === expenseFilter)
                    .filter(expense => {
                      if (!expense.date) return true;
                      const expenseMonth = expense.date.slice(0, 7); // Extract YYYY-MM
                      return expenseMonth === expenseDateFilter;
                    })
                    .map((expense) => (
                    <tr key={expense.id} data-testid={`expense-row-${expense.id}`} className="hover:bg-green-50 transition-colors even:bg-gray-50">
                      <td className="px-2 sm:px-4 py-4 align-middle">
                        <span className="capitalize font-medium text-gray-900">
                          {expense.category === 'bus_maintenance' ? '🚌 Bus Maintenance' : 
                           expense.category === 'fuel' ? '⛽ Fuel' : 
                           expense.category === 'supplies' ? '📚 Supplies' : 
                           expense.category === 'utilities' ? '⚡ Utilities' :
                           expense.category === 'salaries' ? '👥 Salaries' :
                           expense.category === 'maintenance' ? '🔧 Maintenance' :
                           '📋 ' + expense.category}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-2 sm:px-4 py-4 align-middle">
                        <span className="text-gray-700">{expense.description}</span>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-right align-middle">
                        <span className="font-semibold text-gray-900">${expense.amount.toFixed(2)}</span>
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-4 py-4 align-middle">
                        <span className="text-sm text-gray-700">{expense.date}</span>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-center align-middle">
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditExpense(expense)}
                            className="h-8 w-8 p-0 hover:bg-green-50 hover:border-[#1E624A]"
                            data-testid={`edit-expense-${expense.id}-btn`}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 hover:border-red-500 h-8 w-8 p-0"
                            onClick={() => openDeleteExpense(expense)}
                            data-testid={`delete-expense-${expense.id}-btn`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {expenses.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No expenses found
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Invoice Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Edit Invoice</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
            {/* Scrollable Form Content */}
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Student</Label>
                <Input
                  value={editingInvoice ? students.find(s => s.id === editingInvoice.student_id)?.name || "Unknown" : ""}
                  disabled
                  className="bg-gray-50 w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_type" className="text-sm font-medium">Type *</Label>
                <Select
                  value={editForm.type}
                  onValueChange={(value) => setEditForm({ ...editForm, type: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tuition">Tuition</SelectItem>
                    <SelectItem value="books">Books</SelectItem>
                    <SelectItem value="uniform">Uniform</SelectItem>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_amount" className="text-sm font-medium">Amount ($) *</Label>
                <Input
                  id="edit_amount"
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  required
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_description" className="text-sm font-medium">Description</Label>
                <Input
                  id="edit_description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_due_date" className="text-sm font-medium">Due Date *</Label>
                <Input
                  id="edit_due_date"
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  required
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_status" className="text-sm font-medium">Status *</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">🟡 Pending</SelectItem>
                    <SelectItem value="paid">🟢 Paid</SelectItem>
                    <SelectItem value="endorsed">⚪ Endorsed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {editForm.status === "paid" && (
                <div className="space-y-2">
                  <Label htmlFor="edit_payment_method" className="text-sm font-medium">Payment Method *</Label>
                  <Select
                    value={editForm.payment_method}
                    onValueChange={(value) => setEditForm({ ...editForm, payment_method: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">💵 Cash</SelectItem>
                      <SelectItem value="bank">🏦 Bank</SelectItem>
                      <SelectItem value="zaad">💳 Zaad</SelectItem>
                      <SelectItem value="edahab">📱 E-Dahab</SelectItem>
                      <SelectItem value="other">🧾 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Sticky Footer Buttons */}
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935] min-w-[120px]"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={isEditExpenseDialogOpen} onOpenChange={setIsEditExpenseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>✏️ Edit Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditExpenseSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_expense_category">Category *</Label>
              <Select
                value={editExpenseForm.category}
                onValueChange={(value) =>
                  setEditExpenseForm({ ...editExpenseForm, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplies">📚 Supplies</SelectItem>
                  <SelectItem value="utilities">⚡ Utilities</SelectItem>
                  <SelectItem value="salaries">👥 Salaries</SelectItem>
                  <SelectItem value="maintenance">🔧 Maintenance</SelectItem>
                  <SelectItem value="bus_maintenance">🚌 Bus Maintenance</SelectItem>
                  <SelectItem value="fuel">⛽ Fuel</SelectItem>
                  <SelectItem value="other">📋 Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_expense_amount">Amount ($) *</Label>
              <Input
                id="edit_expense_amount"
                type="number"
                step="0.01"
                value={editExpenseForm.amount}
                onChange={(e) =>
                  setEditExpenseForm({ ...editExpenseForm, amount: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_expense_description">Description *</Label>
              <Input
                id="edit_expense_description"
                value={editExpenseForm.description}
                onChange={(e) =>
                  setEditExpenseForm({ ...editExpenseForm, description: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_expense_date">Date *</Label>
              <Input
                id="edit_expense_date"
                type="date"
                value={editExpenseForm.date}
                onChange={(e) =>
                  setEditExpenseForm({ ...editExpenseForm, date: e.target.value })
                }
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditExpenseDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935]"
              >
                💾 Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Dialog */}
      <Dialog open={isDeleteExpenseDialogOpen} onOpenChange={setIsDeleteExpenseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🗑️ Delete Expense</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this expense? This action cannot be undone.
            </p>
            {deletingExpense && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">Category:</span>
                  <span className="capitalize">{deletingExpense.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Amount:</span>
                  <span>${deletingExpense.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Description:</span>
                  <span>{deletingExpense.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date:</span>
                  <span>{deletingExpense.date}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteExpenseDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteExpense}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              🗑️ Delete Expense
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={isEditExpenseDialogOpen} onOpenChange={setIsEditExpenseDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-[#1E624A] to-[#266653] text-white">
            <DialogTitle className="text-xl flex items-center gap-2">
              <Edit size={24} />
              ✏️ Edit Expense
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditExpenseSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div>
                <Label htmlFor="edit_expense_category">Category *</Label>
                <Select
                  value={editExpenseForm.category}
                  onValueChange={(value) =>
                    setEditExpenseForm({ ...editExpenseForm, category: value })
                  }
                >
                  <SelectTrigger data-testid="edit-expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplies">📚 Supplies</SelectItem>
                    <SelectItem value="utilities">⚡ Utilities</SelectItem>
                    <SelectItem value="salaries">👥 Salaries</SelectItem>
                    <SelectItem value="maintenance">🔧 Maintenance</SelectItem>
                    <SelectItem value="bus_maintenance">🚌 Bus Maintenance</SelectItem>
                    <SelectItem value="fuel">⛽ Fuel</SelectItem>
                    <SelectItem value="other">📋 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_expense_amount">Amount ($) *</Label>
                <Input
                  id="edit_expense_amount"
                  type="number"
                  step="0.01"
                  value={editExpenseForm.amount}
                  onChange={(e) =>
                    setEditExpenseForm({ ...editExpenseForm, amount: e.target.value })
                  }
                  required
                  data-testid="edit-expense-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_expense_description">Description *</Label>
                <Input
                  id="edit_expense_description"
                  value={editExpenseForm.description}
                  onChange={(e) =>
                    setEditExpenseForm({ ...editExpenseForm, description: e.target.value })
                  }
                  required
                  data-testid="edit-expense-description-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_expense_date">Date *</Label>
                <Input
                  id="edit_expense_date"
                  type="date"
                  value={editExpenseForm.date}
                  onChange={(e) =>
                    setEditExpenseForm({ ...editExpenseForm, date: e.target.value })
                  }
                  required
                  data-testid="edit-expense-date-input"
                />
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditExpenseDialogOpen(false)}
                data-testid="cancel-edit-expense-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935]"
                data-testid="save-edit-expense-btn"
              >
                💾 Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Dialog */}
      <Dialog open={isDeleteExpenseDialogOpen} onOpenChange={setIsDeleteExpenseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={24} />
              🗑️ Delete Expense
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this expense? This action cannot be undone.
            </p>
            {deletingExpense && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">Category:</span>
                  <span className="capitalize flex items-center gap-1">
                    {deletingExpense.category === 'bus_maintenance' ? '🚌 Bus Maintenance' : 
                     deletingExpense.category === 'fuel' ? '⛽ Fuel' : 
                     deletingExpense.category === 'supplies' ? '📚 Supplies' : 
                     deletingExpense.category === 'utilities' ? '⚡ Utilities' :
                     deletingExpense.category === 'salaries' ? '👥 Salaries' :
                     deletingExpense.category === 'maintenance' ? '🔧 Maintenance' :
                     '📋 ' + deletingExpense.category}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Amount:</span>
                  <span className="font-semibold">${deletingExpense.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Description:</span>
                  <span className="text-right">{deletingExpense.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date:</span>
                  <span>{deletingExpense.date}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteExpenseDialogOpen(false)}
              data-testid="cancel-delete-expense-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteExpense}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-expense-btn"
            >
              🗑️ Delete Expense
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Finance;
